import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlay, FiPause, FiVolume2, FiVolumeX, FiSkipBack, FiSkipForward } = FiIcons;

/* ── Animated equalizer bars ─────────────────────────────────────────── */
/**
 * `color` MUST be a single `bg-*` Tailwind class (e.g. "bg-rose-400").
 * Never pass mixed text-* + bg-* classes here.
 */
export const EqualizerBars = ({ color = 'bg-accent-gold' }) => (
  <span className="flex items-end gap-[2px] h-4 w-5">
    {[0.4, 0.8, 0.55, 1, 0.65].map((h, i) => (
      <motion.span
        key={i}
        className={`w-[3px] rounded-full ${color}`}
        animate={{ scaleY: [h, 1, h * 0.6, 0.9, h] }}
        transition={{
          duration: 0.8 + i * 0.15,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: i * 0.1,
        }}
        style={{ transformOrigin: 'bottom', height: '100%' }}
      />
    ))}
  </span>
);

/* ── Volume Slider ────────────────────────────────────────────────────── */
export const VolumeSlider = ({ volume, onChange }) => (
  <div className="flex items-center gap-2 px-1">
    <SafeIcon icon={FiVolumeX} className="w-3 h-3 text-text-muted flex-shrink-0" />
    <input
      type="range"
      min="0"
      max="1"
      step="0.01"
      value={volume}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="flex-1 h-1 rounded-full appearance-none cursor-pointer accent-accent-gold"
      style={{
        background: `linear-gradient(to right, #c9a84c ${volume * 100}%, #e2e0dc ${volume * 100}%)`,
      }}
    />
    <SafeIcon icon={FiVolume2} className="w-3 h-3 text-text-muted flex-shrink-0" />
  </div>
);

/* ── Track Progress Bar ───────────────────────────────────────────────── */
const ProgressBar = ({ audioRef, isPlaying }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const el = audioRef.current;
      if (el && el.duration) setProgress((el.currentTime / el.duration) * 100);
    }, 500);
    return () => clearInterval(id);
  }, [isPlaying, audioRef]);

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = (e.clientX - rect.left) / rect.width;
    el.currentTime = pct * el.duration;
    setProgress(pct * 100);
  };

  return (
    <div
      onClick={seek}
      className="w-full h-1.5 bg-brand-light rounded-full cursor-pointer overflow-hidden group"
    >
      <motion.div
        className="h-full bg-accent-gold rounded-full"
        style={{ width: `${progress}%` }}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
};

/* ── MusicPlayerWidget ────────────────────────────────────────────────── */
const MusicPlayerWidget = ({
  audioRef,
  track,
  isPlaying,
  isLoading,
  volume,
  onTogglePlay,
  onVolume,
  onPrev,
  onNext,
  trackIndex = 0,
  trackTotal = 1,
  loadError = false,
  compact = false,
}) => {
  if (!track) return null;

  /* ── Compact pill ── */
  if (compact) {
    return (
      <motion.button
        onClick={onTogglePlay}
        whileTap={{ scale: 0.95 }}
        className={`h-12 px-5 rounded-full flex items-center gap-3 shadow-xl transition-all duration-300 border ${
          isPlaying
            ? 'bg-accent-navy text-white border-accent-navy shadow-accent-navy/25'
            : 'bg-white text-accent-navy border-brand-dark hover:border-accent-gold hover:shadow-accent-gold/10'
        }`}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
        ) : isPlaying ? (
          <EqualizerBars color="bg-white" />
        ) : (
          <SafeIcon icon={FiPlay} className="w-4 h-4" />
        )}
        <span className="text-[10px] font-black uppercase tracking-widest hidden md:inline whitespace-nowrap">
          {isLoading ? 'Loading…' : isPlaying ? track.label : 'Play Ambience'}
        </span>
      </motion.button>
    );
  }

  /* ── Full player card ── */
  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-brand-dark p-4 space-y-3">
      {/* Track info row */}
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${track.bg}`}>
          {isPlaying
            ? <EqualizerBars color={track.barColor ?? 'bg-accent-gold'} />
            : <SafeIcon icon={track.icon} className={`w-5 h-5 ${track.color}`} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black text-accent-navy truncate">{track.label}</p>
          <p className="text-[9px] text-text-muted truncate">{track.description}</p>
        </div>
        <span className="text-[9px] text-text-muted font-mono shrink-0">
          {trackIndex + 1}/{trackTotal}
        </span>
      </div>

      {/* Progress bar */}
      <ProgressBar audioRef={audioRef} isPlaying={isPlaying} />

      {/* Playback controls */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onPrev}
          className="p-2 rounded-full hover:bg-brand-light text-text-muted hover:text-accent-navy transition-colors"
          title="Previous track"
        >
          <SafeIcon icon={FiSkipBack} className="w-4 h-4" />
        </button>

        <button
          onClick={onTogglePlay}
          disabled={isLoading}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-md ${
            isLoading
              ? 'bg-brand-light text-text-muted cursor-wait'
              : isPlaying
              ? 'bg-accent-navy text-white hover:bg-accent-navy/90'
              : 'bg-accent-gold text-white hover:bg-accent-gold/90'
          }`}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
          ) : (
            <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="w-4 h-4" />
          )}
        </button>

        <button
          onClick={onNext}
          className="p-2 rounded-full hover:bg-brand-light text-text-muted hover:text-accent-navy transition-colors"
          title="Next track"
        >
          <SafeIcon icon={FiSkipForward} className="w-4 h-4" />
        </button>
      </div>

      {/* Volume slider */}
      <VolumeSlider volume={volume} onChange={onVolume} />

      {/* Error state */}
      {loadError && (
        <p className="text-[9px] text-red-500 font-bold text-center bg-red-50 rounded-xl py-2 px-3">
          Unable to load audio — try another track.
        </p>
      )}
    </div>
  );
};

export default MusicPlayerWidget;